import React, { useMemo } from 'react';
import dayjs, { Dayjs } from 'lib/dayjs';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

const defaultTimeFormatter = (t: number | Dayjs) => dayjs(t).format('YYYY-MM-DD HH:mm:ss');

export interface MessageProps {
  className?: string;
  layout?: 'left' | 'right' | 'mid';
  avatar?: React.ReactNode;
  time: number | Dayjs;
  timeFormatter?: (t: number | Dayjs) => string;
  name: React.ReactNode;
  content: React.ReactNode;
  footer?: React.ReactNode;
}

const layoutFlex = (props: { layout: MessageProps['layout'] }) => {
  const { layout } = props;
  let justifyContent = 'flex-start';
  let flexDirection = '';
  if (layout === 'right') {
    justifyContent = '';
    flexDirection = 'row-reverse';
  }
  if (layout === 'mid') {
    justifyContent = 'flex-middle';
  }
  return css`
    display: flex;
    justify-content: ${justifyContent};
    flex-direction: ${flexDirection};
    padding: 0 4px;
  `;
};

export const LineWrapper = styled.div`
  position: relative;
  padding: 8px;
  margin: 0 0 20px;
  ${layoutFlex}
`;
export const MessageBody = styled.div<{ layout: MessageProps['layout'] }>`
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  align-items: ${({ layout }) => (layout === 'right' ? 'flex-end' : 'flex-start')};
  position: relative;
  margin: 0 8px;
`;
const Header = styled.div<{ layout: MessageProps['layout'] }>`
  /* position: absolute;
  top: -1.8em; */
  white-space: nowrap;
  align-items: center;
  ${layoutFlex}
  ${({ layout }) => {
    const post = layout === 'right' ? 'right' : 'left';
    const justifyContent = layout === 'left' ? 'flex-start' : 'flex-end';
    return {
      [post]: 0,
      justifyContent,
    };
  }}
`;
const Name = styled.span`
  color: #84868c;
`;

const Timer = styled.span<{ layout: MessageProps['layout'] }>`
  padding: 0 4px;
  display: block;
  color: rgba(0, 0, 0, 0.35);
  ${LineWrapper}:hover & {
    display: inline-block;
  }
`;

const AvatarWrapper = styled.div<{ layout: MessageProps['layout'] }>`
  flex: 0 0 auto;
  position: relative;
  width: 32px;
  height: 32px;
  line-height: 32px;
  text-align: center;
  border-radius: 2px;
  overflow: hidden;
  cursor: pointer;
  ${({ layout }) => {
    const layoutRight = layout === 'right';
    const backgroundColor = layoutRight ? '#D6E8FF' : '#fff';
    // const color = layoutRight ? ''
    return {
      padding: layout === 'right' ? 0 : '2px',
      '--avatar-name-bg': backgroundColor,
    };
  }}
`;

const Message: React.FC<MessageProps> = ({
  className,
  layout = 'left',
  avatar,
  time,
  name,
  content,
  footer,
  timeFormatter = defaultTimeFormatter,
}) => {
  const timeString = useMemo(() => {
    return timeFormatter(time);
  }, [time, timeFormatter]);
  const avatarMemo = useMemo(() => {
    if (layout === 'mid') {
      return null;
    }
    return <AvatarWrapper layout={layout}>{avatar}</AvatarWrapper>;
  }, [avatar, layout]);
  return (
    <LineWrapper layout={layout} className={className}>
      {avatarMemo}
      <MessageBody layout={layout}>
        <Header layout={layout}>
          <Name>{name}</Name>
          <Timer layout={layout}>{timeString}</Timer>
        </Header>
        {content}
        {footer}
      </MessageBody>
    </LineWrapper>
  );
};

export default Message;

export { default as TextMessage } from './TextMessage';
export * from './TextMessage';

export { default as StickerMessage } from './StickerMessage';
export * from './StickerMessage';

export { default as ItemCardMessage } from './ItemCardMessage';
export * from './ItemCardMessage';

export { default as ImageMessage } from './ImageMessage';
export * from './ImageMessage';

export { default as VideoMessage } from './VideoMessage';
export * from './VideoMessage';

export { default as OrderCardMessage } from './OrderCardMessage';
export * from './OrderCardMessage';

export { default as SystemMessage } from './SystemMessage';
export * from './SystemMessage';

export { default as FollowCardMessage } from './FollowCardMessage';
export * from './FollowCardMessage';

export { default as BundleMessage } from './BundleMessage';

export { default as VoucherMessage } from './VoucherMessage';
export * from './VoucherMessage';

export { default as VoucherMessageLazada } from './VoucherMessageLazada';
export * from './VoucherMessageLazada';

export { default as VoucherMessageTiktok } from './VoucherMessageTiktok';
export * from './VoucherMessageTiktok';

export { default as RefundMessage } from './RefundMessage';
export * from './RefundMessage';

export { default as TextVideoMessage } from './TextVideoMessage';
export * from './TextVideoMessage';

export { default as OfferCardMessage } from './OfferCardMessage';
export { default as NonsupportMessage } from './NonsupportMessage';
export * from './OfferCardMessage';
